/**
 * Node Data Quality Configuration
 *
 * This is the single file to edit when tuning quality scoring.
 * - weight: max points this field contributes (all fields in a node type must sum to 100)
 * - wordTarget: how many words earns the full weight (proportional below this, capped at weight)
 * - aiThreshold: minimum score (0–100) to unlock AI generation for that node type
 */

export interface FieldQualityConfig {
  /** Field name as it appears in ReactFlowNodeData */
  field: string
  /** Human-readable label for tooltips */
  label: string
  /** This field's maximum contribution to the total score. All fields must sum to 100. */
  weight: number
  /** Words required to earn the full weight. Scoring is proportional below this. */
  wordTarget: number
  /** For array fields: concatenates all items before counting words */
  isArray?: boolean
}

export interface NodeTypeQualityConfig {
  fields: FieldQualityConfig[]
  /**
   * Minimum score (0–100) required to unlock AI child generation.
   * Replaces the legacy MIN_WORD_COUNT binary gate for generatable node types.
   */
  aiThreshold: number
}

export const NODE_QUALITY_CONFIG: Partial<Record<string, NodeTypeQualityConfig>> = {
  venture: {
    aiThreshold: 40,
    fields: [
      { field: 'label', label: 'Overview', weight: 15, wordTarget: 8 },
      {
        field: 'businessModelAndValueProp',
        label: 'What are you building?',
        weight: 25,
        wordTarget: 20,
      },
      { field: 'productType', label: 'Product Type', weight: 10, wordTarget: 3 },
      { field: 'capabilitiesAndAssets', label: 'Available Strengths', weight: 20, wordTarget: 15 },
      { field: 'goals', label: 'Who is it for?', weight: 20, wordTarget: 12 },
      // hardConstraints removed from quality scoring — field no longer shown in UI for new ventures
      // companyName is intentionally excluded — it's an identifier, not substantive context
    ],
  },

  need: {
    aiThreshold: 40,
    fields: [{ field: 'label', label: 'Unmet need', weight: 100, wordTarget: 18 }],
  },

  idea: {
    aiThreshold: 30,
    fields: [{ field: 'label', label: 'Idea', weight: 100, wordTarget: 10 }],
  },
}
